import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactCrop from 'react-image-crop';
import classNames from 'classnames';
import { bem } from 'helpers/class-name';
import { reportError } from 'helpers/error-logging';
import Message from 'components/Message';
import { photosMessages as messages } from '../VenuePage.messages';
import { getImage } from './VenuePhotos.helpers';
import './CropperModal.scss';

const DEFAULT_FILE_TYPE = 'image/jpeg';
const ACCEPTED_FILE_TYPES = ['image/png', DEFAULT_FILE_TYPE];
const { block, element } = bem('cropper-modal');

const CROPPER_INITIAL_PERCENTAGE = 80;
const CropperModal = ({
  closeModal,
  createErrorBanner,
  cropperAspectRatio,
  fileType,
  imageBeforeCrop,
  imageHeight,
  imageWidth,
  minHeight,
  minWidth,
  onPublishPhoto,
  selectedImageType,
  sidenavCollapsed,
}) => {
  const [cropImageWidth, setCropImageWidth] = useState(0);
  const [cropImageHeight, setCropImageHeight] = useState(0);

  let x;
  let y;
  let height;
  let width;
  // Center the crop box in the middle of the image at 80% of the image size
  if (imageWidth > imageHeight) {
    height = CROPPER_INITIAL_PERCENTAGE;

    // Center vertically
    y = (100 - CROPPER_INITIAL_PERCENTAGE) / 2;
    x = (100 - (CROPPER_INITIAL_PERCENTAGE * cropperAspectRatio) / (imageWidth / imageHeight)) / 2;
  } else {
    width = CROPPER_INITIAL_PERCENTAGE;
    // Center horizontally
    x = (100 - CROPPER_INITIAL_PERCENTAGE) / 2;
    y = (100 - (CROPPER_INITIAL_PERCENTAGE * (1 / cropperAspectRatio)) / (imageHeight / imageWidth)) / 2;
  }

  const [crop, setCrop] = useState({
    aspect: cropperAspectRatio,
    unit: '%',
    x,
    y,
    height,
    width,
  });
  let modalTitle;
  switch (selectedImageType) {
    case 'mobile_horiz':
      modalTitle = <Message message={messages.cropperModal.mobileHoriz} />;
      break;
    default:
      // This default case is redundant but it's a relic from when there were multiple image types
      // Since we might add more image types in the future we'll keep the switch statement for now
      modalTitle = <Message message={messages.cropperModal.mobileHoriz} />;
      break;
  }

  const minCropFracWidth = minWidth / imageWidth;
  const minCropFracHeight = minHeight / imageHeight;
  const minCropWidth = minCropFracWidth * cropImageWidth;
  const minCropHeight = minCropFracHeight * cropImageHeight;

  const getCroppedImage = async () => {
    let image;
    try {
      image = await getImage(imageBeforeCrop);
    } catch (error) {
      reportError(error, 'Failed to get image to crop');
      createErrorBanner(messages.error.genericError);
    }
    const canvas = document.createElement('canvas');
    const scaleX = image.width / cropImageWidth;
    const scaleY = image.height / cropImageHeight;
    const ctx = canvas.getContext('2d');

    const finalWidth = crop.width * scaleX;
    const finalHeight = crop.height * scaleY;

    canvas.width = finalWidth;
    canvas.height = finalHeight;

    ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, finalWidth, finalHeight, 0, 0, finalWidth, finalHeight);

    const imageDataUrl = canvas.toDataURL(ACCEPTED_FILE_TYPES.includes(fileType) ? fileType : DEFAULT_FILE_TYPE);
    return imageDataUrl;
  };

  const handlePublishPhoto = async () => {
    const croppedImage = await getCroppedImage();
    onPublishPhoto({ croppedImage });
    closeModal();
  };

  const onImageLoaded = image => {
    setCropImageHeight(image.height);
    setCropImageWidth(image.width);
  };

  return (
    <div className={classNames(block({ sidenavCollapsed }), element('backdrop'))}>
      <div className={element('outer')}>
        <div className={element('top-bar')}>
          {modalTitle}
          <Message message={messages.cropperModal.close}>
            {alt => (
              <button type="button" onClick={closeModal}>
                <img alt={alt} src={require('static/img/Core/close-button.svg')} />
              </button>
            )}
          </Message>
        </div>

        <div className={element('crop-section')}>
          <div className={element('crop-section-inner')}>
            <ReactCrop
              src={imageBeforeCrop}
              crop={crop}
              onChange={setCrop}
              onImageLoaded={onImageLoaded}
              minWidth={minCropWidth}
              minHeight={minCropHeight}
              keepSelection
            />
          </div>
        </div>

        <div className={element('publish-line')}>
          <Message as="button" className="bt bt--primary" onClick={handlePublishPhoto} message={messages.publish} />
        </div>
      </div>
    </div>
  );
};

CropperModal.propTypes = {
  closeModal: PropTypes.func,
  createErrorBanner: PropTypes.func,
  cropperAspectRatio: PropTypes.number,
  fileType: PropTypes.string,
  imageBeforeCrop: PropTypes.string,
  imageHeight: PropTypes.number,
  imageWidth: PropTypes.number,
  minHeight: PropTypes.number,
  minWidth: PropTypes.number,
  onPublishPhoto: PropTypes.func,
  selectedImageType: PropTypes.string,
  sidenavCollapsed: PropTypes.bool,
};

export default CropperModal;
